import { useState } from "react";

import { Icon } from "@blueprintjs/core";
import uniqBy from "lodash/uniqBy";

import { Box } from "@/components/layout/flexbox";
import {
  USER_INDEX,
  WORKSPACE_GROUP_INDEX,
} from "@/components/pieces/algolia/indexes";
import {
  SharedUserType,
  SharedGroupType,
} from "@/components/pieces/roleplays/sharing/scenario-share/types";
import UnifiedSearch from "@/components/pieces/search/unified-search/UnifiedSearch";
import { SUBTEXT_COLOR } from "@/css/constants";

interface ScenarioShareMultiselectProps {
  placeholder?: string;
  onChange: (users: SharedUserType[], groups: SharedGroupType[]) => void;
}

export function ScenarioShareMultiselect({
  placeholder = "Add people",
  onChange,
}: ScenarioShareMultiselectProps) {
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const processSelections = (selections) => {
    const users = selections.filter(
      (item) => item._index === USER_INDEX || "email" in item,
    );

    const groups = selections.filter(
      (item) =>
        item._index === WORKSPACE_GROUP_INDEX ||
        ("name" in item && !("email" in item)),
    );

    return { users, groups };
  };

  const handleSubmit = (selections) => {
    const { users, groups } = processSelections(selections);

    const uniqueUsers = uniqBy(users, "email");
    setSelectedEmails(uniqueUsers);

    const uniqueGroups = uniqBy(groups, "id");
    setSelectedGroups(uniqueGroups);

    onChange(
      uniqueUsers.map((x: any) => ({
        id: x?.id,
        email: x?.email,
        name: x?.name,
        firstName: x?.firstName,
        lastName: x?.lastName,
        photo: x?.photo,
      })),
      uniqueGroups.map((x: any) => ({
        id: x?.id,
        name: x?.name,
        workspace: x?.workspace || {
          id: x?.workspace?.id || "",
          name: x?.workspace?.name || "",
        },
      })),
    );
  };

  const hasSelectedItem =
    selectedEmails?.length > 0 || selectedGroups?.length > 0;

  return (
    <Box css={{ position: "relative" }}>
      <Icon
        icon="search"
        color={SUBTEXT_COLOR}
        style={{
          position: "absolute",
          left: 10,
          top: 16,
          zIndex: 99,
          display: hasSelectedItem ? "none" : "block",
        }}
      />

      <UnifiedSearch
        selections={[...selectedEmails, ...selectedGroups]}
        onChange={handleSubmit}
        existedEmails={[]}
        invalidEmails={[]}
        placeholder={placeholder}
        canBeInvitedEmails={[]}
        pendingInvEmails={[]}
        inputStyle={{
          fontSize: 16,
          paddingLeft: hasSelectedItem ? 0 : 30,
          lineHeight: "24px",
        }}
      />
    </Box>
  );
}
